import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FunnelFilters {
  businessUnit?: string | null;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

export interface TimeComparisonRow {
  stage: string;
  outcome: string;
  total_deals: number;
  avg_days: number;
  median_days: number;
}

export interface LossAnalysisRow {
  lost_from_stage: string;
  total_lost: number;
  percent_of_losses: number;
}

export interface ConsultantConversionRow {
  consultant_id: string;
  consultant_name: string;
  total_deals: number;
  won: number;
  lost: number;
  conversion_rate: number;
  avg_ticket: number;
}

export interface SDRConversionRow {
  sdr_id: string;
  sdr_name: string;
  total_leads: number;
  contacted: number;
  scheduled: number;
  contact_rate: number;
  schedule_rate: number;
}

export interface StageConversionRow {
  stage: string;
  total_entered: number;
  advanced_to_next: number;
  went_to_lost: number;
  conversion_rate: number;
  loss_rate: number;
}

const formatDate = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  return date.toISOString().split('T')[0];
};

export function useFunnelTimeComparison(filters: FunnelFilters) {
  return useQuery({
    queryKey: ['funnel-time-comparison', filters.businessUnit, formatDate(filters.dateFrom), formatDate(filters.dateTo)],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_funnel_time_comparison', {
        p_business_unit: filters.businessUnit || null,
        p_date_from: formatDate(filters.dateFrom),
        p_date_to: formatDate(filters.dateTo),
      });
      if (error) throw error;
      return data as TimeComparisonRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFunnelLossAnalysis(filters: FunnelFilters) {
  return useQuery({
    queryKey: ['funnel-loss-analysis', filters.businessUnit, formatDate(filters.dateFrom), formatDate(filters.dateTo)],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_funnel_loss_analysis', {
        p_business_unit: filters.businessUnit || null,
        p_date_from: formatDate(filters.dateFrom),
        p_date_to: formatDate(filters.dateTo),
      });
      if (error) throw error;
      return data as LossAnalysisRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFunnelConversionByConsultant(filters: FunnelFilters) {
  return useQuery({
    queryKey: ['funnel-conversion-consultant', filters.businessUnit, formatDate(filters.dateFrom), formatDate(filters.dateTo)],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_funnel_conversion_by_consultant', {
        p_business_unit: filters.businessUnit || null,
        p_date_from: formatDate(filters.dateFrom),
        p_date_to: formatDate(filters.dateTo),
      });
      if (error) throw error;
      return data as ConsultantConversionRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFunnelConversionBySDR(filters: FunnelFilters) {
  return useQuery({
    queryKey: ['funnel-conversion-sdr', filters.businessUnit, formatDate(filters.dateFrom), formatDate(filters.dateTo)],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_funnel_conversion_by_sdr', {
        p_business_unit: filters.businessUnit || null,
        p_date_from: formatDate(filters.dateFrom),
        p_date_to: formatDate(filters.dateTo),
      });
      if (error) throw error;
      return data as SDRConversionRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useFunnelStageConversion(filters: FunnelFilters) {
  return useQuery({
    queryKey: ['funnel-stage-conversion', filters.businessUnit, formatDate(filters.dateFrom), formatDate(filters.dateTo)],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_funnel_stage_conversion', {
        p_business_unit: filters.businessUnit || null,
        p_date_from: formatDate(filters.dateFrom),
        p_date_to: formatDate(filters.dateTo),
      });
      if (error) throw error;
      return data as StageConversionRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
